import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowLeft, CheckCircle2, Circle, Award, Clock, FileText, Loader2 } from "lucide-react";

export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState({ lessons_completed: [], exam_passed: false });
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/learning/courses/${slug}`);
      setCourse(data.course);
      setProgress(data.progress);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, [slug]);

  const markComplete = async (lessonId) => {
    setBusy(true);
    try {
      await api.post(`/learning/courses/${slug}/lesson-complete`, { lesson_id: lessonId });
      setProgress((p) => ({ ...p, lessons_completed: [...new Set([...(p.lessons_completed || []), lessonId])] }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!course) return <div className="min-h-screen bg-black text-white"><Navbar /><div className="p-12 text-sm text-neutral-500">Loading…</div></div>;

  const lessons = (course.lessons || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  const lesson = lessons[active] || lessons[0];
  const allDone = lessons.every((l) => progress.lessons_completed?.includes(l.id));
  const completedDone = progress.lessons_completed?.includes(lesson?.id);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        <Link to="/courses" className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white mb-4">
          <ArrowLeft size={12} /> All courses
        </Link>
        <div className="text-overline mb-2">{course.category}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight mb-2">{course.title}</h1>
        <p className="text-sm text-neutral-400 max-w-3xl mb-3">{course.summary}</p>
        <div className="flex items-center gap-4 text-[13px] font-mono-tech uppercase tracking-widest text-neutral-500 mb-8">
          <span className="flex items-center gap-1"><Award size={11} /> {course.certification_name} <span className="text-amber-500">+{course.score_boost}</span></span>
          <span className="flex items-center gap-1"><Clock size={11} /> ~{course.estimated_minutes} min</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-px bg-white/5 border border-white/5">
          {/* Lessons sidebar */}
          <aside className="bg-[#0c0c0c] p-5">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-mono-tech mb-3">Lessons</div>
            <ul className="space-y-1">
              {lessons.map((l, i) => {
                const done = progress.lessons_completed?.includes(l.id);
                return (
                  <li key={l.id}>
                    <button onClick={() => setActive(i)} data-testid={`lesson-btn-${l.id}`}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border ${i === active ? "border-amber-500/60 bg-amber-500/5" : "border-transparent hover:border-white/15"}`}>
                      {done ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0" /> : <Circle size={14} className="text-neutral-500 shrink-0" />}
                      <span className="truncate">{l.title}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <button onClick={() => navigate(`/courses/${slug}/exam`)} disabled={!allDone || progress.exam_passed}
              data-testid="start-exam-btn"
              className="w-full mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 text-black text-xs font-semibold uppercase tracking-widest hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed">
              {progress.exam_passed ? "✓ Certified" : "Take Exam"}
            </button>
            {!allDone && !progress.exam_passed && (
              <div className="text-xs text-neutral-500 mt-2 font-mono-tech">Complete all lessons to unlock the exam</div>
            )}
          </aside>

          {/* Lesson body */}
          {lesson && (
            <section className="bg-[#0A0A0A] p-7">
              <h2 className="font-display text-2xl font-medium mb-4" data-testid="lesson-title">{lesson.title}</h2>
              <div className="prose prose-invert max-w-none text-sm text-neutral-200 leading-relaxed whitespace-pre-wrap" data-testid="lesson-body">
                {lesson.body}
              </div>
              {(lesson.resources || []).length > 0 && (
                <div className="mt-6 pt-5 border-t border-white/5">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 font-mono-tech mb-2">Resources</div>
                  {lesson.resources.map((r, i) => (
                    <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                      className="block text-sm text-amber-500 hover:underline flex items-center gap-2 mb-1">
                      <FileText size={12} /> {r.title}
                    </a>
                  ))}
                </div>
              )}
              <div className="mt-7 flex items-center justify-between">
                {!completedDone ? (
                  <button onClick={() => markComplete(lesson.id)} disabled={busy}
                    data-testid="complete-lesson-btn"
                    className="px-4 py-2.5 bg-amber-500 text-black text-xs font-semibold uppercase tracking-widest hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-2">
                    {busy && <Loader2 size={12} className="animate-spin" />}
                    Mark Complete →
                  </button>
                ) : (
                  <span className="text-xs font-mono-tech uppercase tracking-widest text-emerald-400 inline-flex items-center gap-1">
                    <CheckCircle2 size={12} /> Completed
                  </span>
                )}
                {active < lessons.length - 1 && (
                  <button onClick={() => setActive(active + 1)}
                    className="text-xs font-mono-tech uppercase tracking-widest text-amber-500 hover:underline">
                    Next lesson →
                  </button>
                )}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
